import type { FC } from 'react'
import styled from '@emotion/styled'

import Image from '@segment/matcha/components/Image'
import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'

import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import ButtonText from '@segment/matcha/components/ButtonText'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig, type PageConfig } from '@segment/contexts/page'

import PersonalizationEnginePng2x from './assets/PersonalizationEngine@2x.png'
import PersonalizationEngineWebp from './assets/PersonalizationEngine.webp'
import PersonalizationEngineWebp2x from './assets/PersonalizationEngine@2x.webp'

const getCopy = (pageConfig: PageConfig) => ({
  overline: `CustomerAI Personalization Engine`,
  heading: `Personalize LLMs with your customer data`,
  list: [
    {
      heading: `CustomerAI Personalization Engine`,
      description: `Coming soon: Infuse greater customer understanding and personalization into every customer interaction with a seamless API connection between your Segment customer data and ChatGPT or other large language models (LLMs).`,
      cta: (
        <p>
          <ButtonText
            iconEnd={ChevronRightIcon}
            href={getAbsoluteUrl(`/solutions/ai/customerai-personalization-engine/`, pageConfig)}
          >
            Learn more
          </ButtonText>
        </p>
      ),
    },
  ],
})

const PersonalizationEngine: FC = () => {
  const pageConfig = usePageConfig()
  const copy = getCopy(pageConfig)

  return (
    <SectionBase variant="2Col">
      <SectionCopy>
        <CopyTextBase>
          <CopyText.Overline>{copy.overline}</CopyText.Overline>
          <CopyText.Heading variant="h2" tag="h3">
            {copy.heading}
          </CopyText.Heading>

          {copy.list.map(({ heading, description, cta }) => (
            <FeatureBoxBase key={heading} headerPosition="top" alignment="left">
              <FeatureBox.Body>
                <FeatureBox.Title>{heading}</FeatureBox.Title>
                <FeatureBox.Text>{description}</FeatureBox.Text>
                {cta}
              </FeatureBox.Body>
            </FeatureBoxBase>
          ))}
        </CopyTextBase>
      </SectionCopy>

      <Section.Contents>
        <picture>
          <source type="image/webp" srcSet={`${PersonalizationEngineWebp}, ${PersonalizationEngineWebp2x} 2x`} />
          <StyledImage
            src={PersonalizationEnginePng2x}
            alt="Personalization Engine"
            width="973"
            height="587"
            loading="lazy"
          />
        </picture>
      </Section.Contents>
    </SectionBase>
  )
}

const SectionCopy = styled(Section.Copy)`
  display: flex;
  align-items: center;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    max-width: 440px;
    justify-self: end;
    align-self: center;
  }
`

const StyledImage = styled(Image)`
  filter: drop-shadow(${({ theme }) => theme.shadows[2]});
`

export default PersonalizationEngine
